// HowToVideoModal — one global popup that plays a short how-to video for a
// Go-Live checklist step. Driven entirely by the ?howto=<key> query param, so
// any page (or the checklist) can trigger it just by navigating with that param.
// Mounted once in Layout; renders nothing when the param is absent.
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, PlayCircle, Film } from 'lucide-react';
import { HOW_TO_VIDEOS } from './howToVideos';

// Turn a stored URL into an embeddable player.
//   YouTube / Vimeo  → iframe, fixed 16:9 frame
//   .html / .htm     → a self-contained HTML player you build and drop in public/
//                      (e.g. /howto/leads.html). These render at a fixed pixel
//                      size, so we measure and scale them to fit (see ScaledHtmlFrame).
//   anything else    → a direct video file (.mp4 etc.), 16:9
// Returns null for empty URLs.
function resolvePlayer(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.html?($|[?#])/i.test(url)) return { type: 'html', src: url };
  return { type: 'video', src: url };
}

// A same-origin .html player (dropped in public/) renders at its own fixed pixel
// size and would otherwise overflow the modal with scrollbars on both axes. We
// let it load, read its true size (same origin, so we can look inside), then
// CSS-scale the whole iframe to fit the modal width — no scrollbars, whole
// player visible. The bundle unpacks asynchronously, so we re-measure as it settles.
function ScaledHtmlFrame({ src, title }) {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const [nat, setNat] = useState(null);   // { w, h } player's true pixel size
  const [scale, setScale] = useState(1);

  const measure = () => {
    try {
      const doc = frameRef.current?.contentWindow?.document;
      if (!doc || !doc.body) return;
      // Neutralize the file's full-viewport centering (body{min-height:100vh;
      // display:flex}) so the document collapses to the player's real size instead
      // of reporting the iframe height. Re-added if the bundle wiped the head.
      if (!doc.getElementById('__fit_reset')) {
        const st = doc.createElement('style');
        st.id = '__fit_reset';
        st.textContent = 'html{height:auto!important}body{min-height:0!important;height:auto!important;display:inline-block!important;width:auto!important;vertical-align:top}';
        (doc.head || doc.documentElement).appendChild(st);
      }
      const de = doc.documentElement;
      const w = Math.max(doc.body.scrollWidth, doc.body.offsetWidth, de.scrollWidth);
      const h = Math.max(doc.body.scrollHeight, doc.body.offsetHeight, de.scrollHeight);
      if (w > 60 && h > 60) {
        setNat((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
      }
    } catch {
      /* cross-origin (shouldn't happen for local html) — leave unscaled */
    }
  };

  const handleLoad = () => {
    measure();
    let ro;
    try {
      const doc = frameRef.current?.contentWindow?.document;
      ro = new ResizeObserver(measure);
      if (doc?.documentElement) ro.observe(doc.documentElement);
      if (doc?.body) ro.observe(doc.body);
    } catch { /* ignore */ }
    const timers = [150, 500, 1200, 2500].map((t) => setTimeout(measure, t));
    if (frameRef.current) {
      frameRef.current.__cleanup = () => { ro?.disconnect(); timers.forEach(clearTimeout); };
    }
  };

  // Fit the player's width to the modal frame.
  useEffect(() => {
    const fit = () => {
      if (!nat || !boxRef.current) return;
      setScale(boxRef.current.clientWidth / nat.w);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [nat]);

  useEffect(() => () => frameRef.current?.__cleanup?.(), []);

  return (
    <div
      ref={boxRef}
      className="relative w-full rounded-xl overflow-hidden bg-[#faf9f5]"
      style={nat ? { height: nat.h * scale } : { aspectRatio: '16 / 9' }}
    >
      <iframe
        ref={frameRef}
        title={title}
        src={src}
        onLoad={handleLoad}
        scrolling="no"
        className="absolute top-0 left-0 border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        style={nat
          ? { width: nat.w, height: nat.h, transform: `scale(${scale})`, transformOrigin: 'top left' }
          : { width: '100%', height: '100%' }}
      />
    </div>
  );
}

export default function HowToVideoModal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const key = searchParams.get('howto');
  const step = key ? HOW_TO_VIDEOS[key] : null;

  const close = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('howto');
    setSearchParams(next, { replace: true });
  };

  if (!step) return null;
  const player = resolvePlayer(step.videoUrl);

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-6 pt-5 pb-4 border-b border-slate-100">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-cyan-50 text-cyan-600 shrink-0">
            <PlayCircle className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-[16px] font-bold text-slate-900">{step.title}</h3>
            <p className="text-[13px] text-slate-500 mt-0.5 leading-snug">{step.blurb}</p>
          </div>
          <button onClick={close} className="p-1.5 -mr-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {player?.type === 'html' ? (
            <ScaledHtmlFrame src={player.src} title={step.title} />
          ) : (
            <div className="relative w-full rounded-xl overflow-hidden bg-slate-900" style={{ aspectRatio: '16 / 9' }}>
              {!player ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <Film className="w-9 h-9 text-slate-500 mb-2.5" />
                  <p className="text-[13.5px] font-semibold text-slate-300">Walkthrough video coming soon</p>
                  <p className="text-[12px] text-slate-500 mt-1">In the meantime, follow the steps on this page.</p>
                </div>
              ) : player.type === 'iframe' ? (
                <iframe
                  title={step.title}
                  src={player.src}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video src={player.src} controls autoPlay className="absolute inset-0 w-full h-full" />
              )}
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex justify-end">
          <button
            onClick={close}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
